// The Lettings pages (3 Nov 2026, docs/plans/lettings-ui-simplification.md asks 6 and 7): one address each, as the
// sidebar's Lettings group (lib/nav.ts), each behind its own permission pages (App.tsx — LETTINGS_PAGES in lib/lettings.ts):
//   /app/lettings/viewings      everyone who wants to see a flat (leads) and the viewings booked for them (leads or viewings)
//   /app/lettings/applications  one table of every application on every flat (applications or reservations)
//   /app/lettings/available     the free flats with a published advert, where a tenant is referred from (reservations or listings)
//   /app/lettings/offers        rent requests — rents offered below the advert, and who may decide them (offers)
// No tab row any more: the sidebar is the way between them. Each page has its own row of count tiles at the top, over the
// rows its table loads (the lists' own endpoints, so a tile and its table agree). Clicking a tile puts the filter it names
// in the URL (and When, where it names a period — for that visit, the remembered When is left alone); the tab follows the
// URL while it is open, and clicking the lit tile again takes the params away, back to the default. The Filter's Apply
// drops them too, so a tile is lit only while its filter is the one on screen.
// /app/lettings and /app/lettings?sub=<viewings|applications|available|offers> (older links, emails and the API's own
// links) redirect to the page's address with every other param kept (?flat=, ?lead=, ?feedback=, ?unit=, ?filter=, …);
// the sub-tabs that went before (overview, leads, listings, reservations, tenancies, changeovers) land where those rows
// are now. Since 26 Oct 2026 (later) the reservation sheet is not Lettings' backbone: a flat is bookable because its
// listing is published, and a sheet shows only on its development's page (docs/plans/lettings-one-form.md).
import type { ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../stores/auth';
import { useCount } from '../hooks/useCount';
import { RequirePermission } from '../components/RequirePermission';
import { StatTile } from '../components/charts/StatTile';
import { ViewingsTab } from '../components/lettings/ViewingsTab';
import { ApplicationsTab } from '../components/lettings/ApplicationsTab';
import { AvailableTab } from '../components/lettings/AvailableTab';
import { OffersTab } from '../components/lettings/OffersTab';
import { reservationsApi } from '../lib/reservations';
import { firstLettingsPath, lettingsApi, LETTINGS_PAGES } from '../lib/lettings';
import { api } from '../lib/api';
import { EARLIEST_DAY, LATEST_DAY, londonParts, presetRange } from '../lib/dates';

/** The sub-tabs that went (2026-10-22): a saved link or a dashboard drill lands where those rows are now, with its other params. */
const MOVED_SUBS: Record<string, string> = {
  overview: '', leads: '/app/lettings/viewings?show=requests', listings: '/app/marketing/listings',
  reservations: '/app/lettings/applications', tenancies: '/app/property?sub=tenancies', changeovers: '/app/property?sub=changeovers',
};

/** /app/lettings (?sub=…): the page's own address, every other param kept. */
export default function LettingsRedirect() {
  const { canAccessPage } = useAuth();
  const [params] = useSearchParams();
  const sub = params.get('sub') ?? '';
  const target = MOVED_SUBS[sub] || LETTINGS_PAGES.find((p) => p.key === sub)?.path || firstLettingsPath(canAccessPage);
  if (!target) return <RequirePermission page="applications">{null}</RequirePermission>;
  const [path, q] = target.split('?');
  const next = new URLSearchParams(q);
  params.forEach((v, k) => { if (k !== 'sub' && !next.has(k)) next.set(k, v); });
  const qs = next.toString();
  return <Navigate to={qs ? `${path}?${qs}` : path!} replace />;
}

/**
 * A tile's link: `set` is the params it puts in the URL; `family` every param this page's tiles use, so one tile takes
 * another's off. `on` = the URL holds exactly `set`; `toggle` puts it there, or (lit already, or no `set` — the tile
 * that counts everything) takes the family away.
 */
function useTiles(family: string[]) {
  const [params, setParams] = useSearchParams();
  const on = (set: Record<string, string>) => family.every((k) => (params.get(k) ?? undefined) === set[k]);
  const toggle = (set?: Record<string, string>) => {
    const n = new URLSearchParams(params);
    const lit = !!set && on(set);
    for (const k of [...family, 'view']) n.delete(k);
    if (set && !lit) for (const [k, v] of Object.entries(set)) n.set(k, v);
    setParams(n);
  };
  return { on, toggle };
}
const n = (v: number | undefined) => (v === undefined ? '—' : v);
const Row = ({ children }: { children: ReactNode }) => <div className="mb-2.5 tile-row">{children}</div>;

// ── Viewings ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export function LettingsViewingsPage() {
  return <div><ViewingsTiles /><ViewingsTab /></div>;
}

/**
 * The board's own counts (GET /lettings/viewings, perPage 1), so each tile and the rows it opens agree:
 *   To book               requests with no viewing on the go, any time            → Status New requests, Booked at Any time
 *   This week             booked viewings scheduled Monday to Sunday (London) —   → Status Booked, Booked for This week
 *                         by the scheduled time, as the old "Viewings this week" tile; cancelled ones are not Booked
 *   To mark completed     booked, the time has passed (GET /viewings?feedbackDue)  → Status Booked, any time (the list
 *                         holds the upcoming ones too, newest first; no preset ends "now")
 *   Completed this month  viewings completed, scheduled this month               → Status Completed, Booked for This month
 * Booked at stays Any time for all four. Each tile shows only to a role that can read what it counts.
 */
function ViewingsTiles() {
  const { can } = useAuth();
  const canLeads = can('leads.read');
  const canViewings = can('viewings.read');
  const t = useTiles(['show', 'when', 'for']);
  const counts = (key: string, at: { from?: string; to?: string }, enabled: boolean) => ({
    queryKey: ['/lettings/viewings', 'counts', key],
    queryFn: () => lettingsApi.board({ from: EARLIEST_DAY, to: LATEST_DAY, atFrom: at.from, atTo: at.to, perPage: 1 }),
    enabled,
  });
  const all = useQuery(counts('any', {}, canLeads));
  const week = useQuery(counts('thisWeek', presetRange('thisWeek'), canViewings));
  const month = useQuery(counts('thisMonth', presetRange('thisMonth'), canViewings));
  const toBook = all.data?.counts.requests;
  const thisWeek = week.data?.counts.booked;
  const toComplete = useCount('/viewings', { feedbackDue: true }, canViewings);
  const completed = month.data?.counts.completed;
  if (!canLeads && !canViewings) return null;
  const tile = (set: Record<string, string>) => ({ active: t.on(set), onClick: () => t.toggle(set) });
  return (
    <Row>
      {canLeads && <StatTile compact label="To book" value={n(toBook)} hint="requests with no viewing yet" loading={all.isLoading} status={toBook ? 'warning' : undefined} {...tile({ show: 'requests', when: 'any' })} />}
      {canViewings && <StatTile compact label="This week" value={n(thisWeek)} hint="viewings booked for Monday to Sunday" loading={week.isLoading} {...tile({ show: 'booked', when: 'any', for: 'thisWeek' })} />}
      {canViewings && <StatTile compact label="To mark completed" value={n(toComplete)} hint="the viewing time has passed" status={toComplete ? 'warning' : undefined} {...tile({ show: 'booked', when: 'any' })} />}
      {canViewings && <StatTile compact label="Completed this month" value={n(completed)} hint="viewings done this month" loading={month.isLoading} {...tile({ show: 'completed', when: 'any', for: 'thisMonth' })} />}
    </Row>
  );
}

// ── Applications ─────────────────────────────────────────────────────────────────────────────────────────────────────

export function LettingsApplicationsPage() {
  return <div><ApplicationsTiles /><ApplicationsTab /></div>;
}

/** The Phase groups the tiles count (the same as ApplicationsTab's Phase filter; ?filter= values). Rejected rows are in none. */
const APPLICATION_TILES = [
  { filter: 'review', label: 'Under review', hint: 'waiting for Lotus Living', phases: ['under_review'], warn: true },
  { filter: 'progress', label: 'In progress', hint: 'holding deposit, referencing, agreement', phases: ['referred', 'applying'], warn: false },
  { filter: 'moving', label: 'With Lotus Living', hint: 'deposit, first rent, internal checklist', phases: ['moving_in'], warn: false },
  { filter: 'moved', label: 'Moved in', hint: 'tenancy started', phases: ['moved_in'], warn: false },
] as const;

/**
 * Applications (every live one — not rejected; clicking clears the Phase) · Under review · In progress · With Lotus Living
 * · Moved in, counted from the rows the table loads (the same query, so one request) over any time; each sets the Phase
 * group (?filter=) and Booked at Any time.
 */
function ApplicationsTiles() {
  const q = useQuery({ queryKey: ['/reservations/applications', 'all'], queryFn: () => reservationsApi.applications() });
  const t = useTiles(['filter', 'when']);
  const live = (q.data ?? []).filter((r) => r.reviewStatus !== 'REJECTED');
  const count = (phases: readonly string[]) => (q.data ? live.filter((r) => phases.includes(r.phase)).length : undefined);
  return (
    <Row>
      <StatTile compact label="Applications" value={n(q.data ? live.length : undefined)} hint="live, not rejected" loading={q.isLoading} onClick={() => t.toggle()} />
      {APPLICATION_TILES.map((x) => {
        const v = count(x.phases); const set = { filter: x.filter, when: 'any' };
        return <StatTile key={x.filter} compact label={x.label} value={n(v)} hint={x.hint} loading={q.isLoading} status={x.warn && v ? 'warning' : undefined} active={t.on(set)} onClick={() => t.toggle(set)} />;
      })}
    </Row>
  );
}

// ── Available ────────────────────────────────────────────────────────────────────────────────────────────────────────

export function LettingsAvailablePage() {
  return <div><AvailableTiles /><AvailableTab /></div>;
}

/**
 * Free flats (every free flat with a live advert; clicking clears Available) · Available now (no date, or on or before
 * today) · Coming free (later) · Developments (how many developments they are in; a count, not a filter) — from the rows
 * the table loads (the same query).
 */
function AvailableTiles() {
  const q = useQuery({ queryKey: ['/lettings/available'], queryFn: () => reservationsApi.available() });
  const t = useTiles(['available']);
  const rows = q.data;
  const today = londonParts(new Date().toISOString()).day;
  const now = rows?.filter((f) => !f.availableFrom || f.availableFrom.slice(0, 10) <= today).length;
  const tile = (set: Record<string, string>) => ({ active: t.on(set), onClick: () => t.toggle(set) });
  return (
    <Row>
      <StatTile compact label="Free flats" value={n(rows?.length)} hint="with a live advert" loading={q.isLoading} onClick={() => t.toggle()} />
      <StatTile compact label="Available now" value={n(now)} hint="free to move in today" loading={q.isLoading} {...tile({ available: 'now' })} />
      <StatTile compact label="Coming free" value={n(rows && now !== undefined ? rows.length - now : undefined)} hint="free from a later date" loading={q.isLoading} {...tile({ available: 'later' })} />
      <StatTile compact label="Developments" value={n(rows ? new Set(rows.map((f) => f.propertyId)).size : undefined)} hint="with a free flat" loading={q.isLoading} />
    </Row>
  );
}

// ── Offers ───────────────────────────────────────────────────────────────────────────────────────────────────────────

export function LettingsOffersPage() {
  return <div><OffersTiles /><OffersTab /></div>;
}

type OfferCounts = { PENDING: number; COUNTERED: number; APPROVED: number; REJECTED: number };
const OFFER_TILES = [
  { status: 'PENDING', label: 'Waiting', hint: 'for a decision' },
  { status: 'COUNTERED', label: 'Countered', hint: 'with the applicant' },
  { status: 'APPROVED', label: 'Agreed', hint: 'rent agreed' },
  { status: 'REJECTED', label: 'Declined', hint: 'rent not agreed' },
] as const;

/**
 * Waiting · Needs higher approval (staff: PENDING rent requests further below the advert than the viewer's
 * applications.discountLimitPercent, from the /reservations pipeline counts — it opens Waiting) · Countered · Agreed ·
 * Declined, from GET /reservations/offers' own counts over any time; each sets Status (?status=) and When Any time.
 */
function OffersTiles() {
  const { can } = useAuth();
  const t = useTiles(['status', 'when']);
  const counts = useQuery({
    queryKey: ['/reservations/offers', 'counts'],
    queryFn: async () => ((await api.get<unknown[]>('/reservations/offers', { status: 'all', from: EARLIEST_DAY, to: LATEST_DAY, page: 1, perPage: 1 })).meta as unknown as { counts?: OfferCounts }).counts,
  });
  const staff = can('reservations.update') && can('applications.read') && can('offers.read');
  const pipeline = useQuery({ queryKey: ['/reservations', 'pipeline'], queryFn: () => reservationsApi.pipeline(), enabled: staff });
  const higher = pipeline.data?.counts.needsHigherApproval;
  const waiting = { status: 'PENDING', when: 'any' };
  const tile = (x: (typeof OFFER_TILES)[number]) => {
    const set = { status: x.status, when: 'any' }; const v = counts.data?.[x.status];
    return <StatTile key={x.status} compact label={x.label} value={n(v)} hint={x.hint} loading={counts.isLoading} status={x.status === 'PENDING' && v ? 'warning' : undefined} active={t.on(set)} onClick={() => t.toggle(set)} />;
  };
  return (
    <Row>
      {tile(OFFER_TILES[0])}
      {staff && <StatTile compact label="Needs higher approval" value={n(higher)} hint="discount over your limit" loading={pipeline.isLoading} status={higher ? 'warning' : undefined} onClick={() => { if (!t.on(waiting)) t.toggle(waiting); }} />}
      {OFFER_TILES.slice(1).map(tile)}
    </Row>
  );
}
